import { HTTP_RESPONSE_STATUS_CODES, SKIP_FORBIDDEN_RESPONSE_CONFIG } from 'constants/http';

export class FormsApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getForms() {
    return this.#http.get('/api/Forms');
  }

  async getFormsInfo() {
    return this.#http.get('/api/Forms/info');
  }

  async getFormById(params) {
    return this.#http.get(`/api/Forms`, { params });
  }

  async getFormsByApplicationId({ applicationId }) {
    return this.#http.get(`/api/Forms`, { params: { applicationId } });
  }

  async getFormTemplateContent({ formId, ...params }) {
    return this.#http.get(`/api/Forms/${formId}/template/content`, { ...params });
  }

  async getFormTemplateSignability({ formId }) {
    return this.#http.get(`/api/Forms/${formId}/template/signability`);
  }

  async getFormTemplateMetadata({ formId, ...params }) {
    return this.#http.get(`/api/Forms/${formId}/template/metadata`, { params });
  }

  async getFormTemplateJson({ formId, ...params }) {
    return this.#http.get(`/api/Forms/${formId}/template/json`, {
      params,
      validateStatus: status =>
        status === HTTP_RESPONSE_STATUS_CODES.SUCCESS ||
        status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT,
    });
  }

  async createForm(body) {
    return this.#http.post('/api/Forms', body);
  }

  async createFormPdf({ signal, formData, ...params }) {
    return this.#http.post('/api/Forms', formData, {
      signal,
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
  }

  async deleteForm({ formId }) {
    return this.#http.delete(`/api/Forms/${formId}`);
  }

  async editFormTemplate(body) {
    return this.#http.put(`/api/Forms/${body.formId}/template`, body);
  }

  async replaceFormTemplatePdf({ formId, file }) {
    const formData = new FormData();
    formData.append('file', file);

    return this.#http.post(`/api/Forms/${formId}/template/pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async editForm(body) {
    return this.#http.put(`/api/Forms/${body.formId}`, body);
  }

  async createFormOverride({ formId, formData, signal }) {
    return this.#http.post(`/api/Forms/${formId}/override`, formData, {
      signal,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getFormSchema(params) {
    return this.#http.get('/api/forms/schema', { params });
  }

  async getFormSchemaById({ formId, ...params }) {
    return this.#http.get(`/api/forms/schema/${formId}`, { params });
  }

  async getFormPreviewPdf({ formId, ...params }) {
    return this.#http.get(`/api/forms/${formId}/preview/pdf`, { params, responseType: 'blob' });
  }

  async getFormPreviewSigning({ formId, ...params }) {
    return this.#http.get(`/api/forms/${formId}/preview/signing`, { params });
  }

  async previewPrismFormPdf({ formId, document, margins, pageNumbering, watermark, ...params }) {
    return this.#http.post(
      `/api/forms/${formId}/preview/pdf`,
      { document, margins, pageNumbering, watermark },
      { params, responseType: 'blob' },
    );
  }

  async getGeneratedFormByApplicationId({ formId, applicationId }) {
    return this.#http.get(`/api/forms/applications/${applicationId}/forms/${formId}/generate`, {
      responseType: 'blob',
    });
  }

  async getGeneratedFormByLoanNumber({ formId, loanNumber }) {
    return this.#http.get(`/api/forms/${formId}/loan-number/${loanNumber}/generate`, {
      responseType: 'blob',
    });
  }

  async getFormHistorySnapshotByEntryId({ formId, entryId, ...params }) {
    return this.#http.get(`/api/forms/${formId}/history/${entryId}/snapshot`, { params });
  }

  async getFormHistorySnapshotPdfByEntryId({ formId, entryId, ...params }) {
    return this.#http.get(`/api/forms/${formId}/history/${entryId}/snapshot/pdf`, { params });
  }

  async getFormAuditTemplateChanges({ formId, auditId }) {
    return this.#http.get(
      `/api/forms/${formId}/audit/${auditId}/template-changes`,
      SKIP_FORBIDDEN_RESPONSE_CONFIG,
    );
  }

  async getFormAuditTemplateJson({ formId, auditId, side }) {
    return this.#http.get(
      `/api/forms/${formId}/audit/${auditId}/template/${side}/json`,
      SKIP_FORBIDDEN_RESPONSE_CONFIG,
    );
  }

  /** The PDF or HTML file on one side of a change, as a blob: the call carries the bearer header. */
  async getFormAuditTemplateFile({ formId, auditId, side }) {
    return this.#http.get(`/api/forms/${formId}/audit/${auditId}/template/${side}/file`, {
      ...SKIP_FORBIDDEN_RESPONSE_CONFIG,
      responseType: 'blob',
    });
  }

  async getFormAuditCompare({ formId, auditId }) {
    return this.#http.get(
      `/api/forms/${formId}/audit/${auditId}/compare`,
      SKIP_FORBIDDEN_RESPONSE_CONFIG,
    );
  }

  /** A change's Prism document (`before` / `after`) or the live one (`current`), as a sample PDF. */
  async getFormAuditPreviewPdf({ formId, auditId, side }) {
    return this.#http.get(`/api/forms/${formId}/audit/${auditId}/preview/pdf`, {
      ...SKIP_FORBIDDEN_RESPONSE_CONFIG,
      params: { side },
      responseType: 'blob',
    });
  }
}
